import { useState } from "react"
import {
	IconLink3Outline18 as LinkIcon,
	IconSquareArrowUpRightOutline18 as OpenIcon,
} from "nucleo-ui-outline-18"

import type { CustomerPortalSettings, Workspace } from "@productlane/zero"
import { cn } from "@productlane/lib/src/cn"
import {
	canPublishSupportPortal,
	SUPPORT_PORTAL_PUBLISH_REQUIRED_PLANS,
} from "@productlane/lib/src/plans-client"
import {
	Badge,
	Button,
	CopyButton,
	Loader,
	Select,
	Switch,
} from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import { MetaInfo } from "@/components/meta-info"
import { toast } from "@/components/toast"
import { UpgradeButton } from "@/components/upgrade-button"
import { analytics } from "@/lib/analytics"
import { api } from "@/lib/api"
import { captureError } from "@/lib/capture-error"
import { useRole } from "@/lib/useRole"
import { mutateOk, useZero, useZeroWorkspace } from "@/lib/zero"
import {
	portalHostname,
	showInSelectorToVisibility,
	SWITCHER_OPTIONS,
	visibilityToShowInSelector,
} from "@/screens/settings/portal-instance-form"
import { PortalLogoField } from "@/screens/settings/portal-logo-field"
import { SettingsHeader } from "@/screens/settings/settings-header"
import {
	settingsNavGroupClass,
	SettingsNavRow,
} from "@/screens/settings/settings-pages/settings-nav-row"
import { SettingItem } from "@/screens/settings/settings-switches/setting-item"
import { SettingsContainer } from "@/screens/settings/settings-switches/settings-container"
import { SettingsSection } from "@/screens/settings/settings-switches/settings-section"
import { usePortalLogoUpload } from "@/screens/settings/use-portal-logo-upload"
import { PortalContentCard } from "./portal-content-card"
import { PortalLandingSelect } from "./portal-landing-select"
import {
	PortalAuthSection,
	PortalLinksSection,
} from "./portal-override-sections"
import { RenameSubdomainDialog } from "./rename-subdomain-dialog"

type WorkspaceWithPortal = Workspace & {
	customerPortalSettings?: CustomerPortalSettings | null
}

/**
 * The main portal at /settings/portal/instances/main. It has no PortalInstance
 * row: its logo is the workspace logo, its URL is the unprefixed domain, and its
 * sections are the workspace publish flags.
 */
export function MainPortalPage() {
	const workspace = useZeroWorkspace()

	if (!workspace) {
		return (
			<>
				<SettingsHeader title="Main portal" />
				<SettingsContainer>
					<div className="flex items-center justify-center py-8">
						<Loader />
					</div>
				</SettingsContainer>
			</>
		)
	}

	return <MainPortal workspace={workspace} />
}

function MainPortal({ workspace }: { workspace: WorkspaceWithPortal }) {
	const zero = useZero()
	const { role } = useRole()
	const uploadLogo = usePortalLogoUpload()
	const planQuery = api.workspace.activePlan.useQuery(undefined, {
		staleTime: 60_000,
	})
	const [preview, setPreview] = useState<string | null>(null)
	const [renameOpen, setRenameOpen] = useState(false)

	const hostname = portalHostname(workspace)
	const portalUrl = `https://${hostname}`

	const isSupportPublished =
		workspace.customerPortalSettings?.isEnabled ?? false
	const canPublishSupport = canPublishSupportPortal({
		plan: planQuery.data,
		isCurrentlyPublished: isSupportPublished,
	})

	const saveLogo = async (logoUrl: string) => {
		try {
			await mutateOk(
				zero.mutate(
					mutators.workspaces.update({
						id: workspace.id,
						name: workspace.name,
						logoUrl,
					}),
				),
			)
		} catch (e) {
			setPreview(null)
			captureError(e, {
				domain: "portal",
				action: "update workspace logo",
				workspaceId: workspace.id,
			})
			toast.error({
				title: "Your logo did not save",
				description: e instanceof Error ? e.message : "Try uploading it again.",
			})
		}
	}

	const publish = async (
		args: Parameters<typeof mutators.workspaces.update>[0],
		event: string,
		value: boolean,
	) => {
		try {
			await mutateOk(zero.mutate(mutators.workspaces.update(args)))
			analytics.track(event, { value, enabled: true })
		} catch (e) {
			captureError(e, {
				domain: "portal",
				action: "publish portal section",
				workspaceId: workspace.id,
			})
			toast.error({
				title: "Your change did not save",
				description: e instanceof Error ? e.message : "Try again.",
			})
		}
	}

	return (
		<>
			<MetaInfo title="Main portal" />

			<SettingsHeader
				title="Main portal"
				subtitle={hostname}
				titleSuffix={<Badge variant="green">Default</Badge>}
				action={
					<Button
						variant="outline"
						Icon={OpenIcon}
						onClick={() => window.open(portalUrl, "_blank")}
					>
						Open portal
					</Button>
				}
			/>

			<SettingsContainer className="flex flex-col gap-4 divide-y-0">
				<SettingItem
					title="Logo"
					description="Shown in the portal header and in emails."
					action={
						<PortalLogoField
							size="md"
							logoUrl={preview ?? workspace.logoUrl}
							disabled={role === "VIEWER"}
							label="Upload a logo for the main portal"
							onPicked={(file, previewUrl) => {
								setPreview(previewUrl)
								void uploadLogo(file)
									.then((fileUrl) => saveLogo(fileUrl))
									.catch((e: unknown) => {
										setPreview(null)
										toast.error({
											title: "Your logo did not upload",
											description:
												e instanceof Error
													? e.message
													: "Try uploading it again.",
										})
									})
							}}
						/>
					}
				/>

				<SettingItem
					title="URL"
					description={
						workspace.customDomain
							? `Served on your own domain. The Productlane address is ${workspace.domain}.productlane.com.`
							: "The main portal serves the domain itself, with no slug."
					}
					action={
						<span className="flex min-w-0 items-center gap-1">
							<span className="truncate font-mono text-xs text-secondary">
								{hostname}
							</span>
							<CopyButton
								value={portalUrl}
								className="!text-tertiary hover:!text-primary"
							/>
							{role === "ADMIN" && (
								<Button
									variant="outline"
									size="sm"
									className="ml-1 flex-none"
									onClick={() => setRenameOpen(true)}
								>
									Change
								</Button>
							)}
						</span>
					}
				/>
				<RenameSubdomainDialog
					isOpen={renameOpen}
					onOpenChange={setRenameOpen}
					current={workspace.domain}
					rootDomain="productlane.com"
				/>

				<SettingItem
					title="Portal switcher"
					description="Visitors on this portal can switch to your other portals."
					action={
						<Select
							name="portalSwitcherVisible"
							className="max-w-60"
							selectClassName="dark:bg-gray-700/70"
							disabled={role === "VIEWER"}
							value={showInSelectorToVisibility(
								workspace.portalSwitcherVisible ?? true,
							)}
							onChange={(value) => {
								const portalSwitcherVisible = visibilityToShowInSelector(
									value === "hidden" ? "hidden" : "shown",
								)
								void publish(
									{ id: workspace.id, portalSwitcherVisible },
									"toggle-main-portal-switcher",
									portalSwitcherVisible,
								)
							}}
							options={SWITCHER_OPTIONS.map(({ value, label, Icon }) => ({
								value,
								label: (
									<span className="flex items-center gap-2">
										<Icon className="size-4 flex-none text-tertiary" />
										{label}
									</span>
								),
							}))}
						/>
					}
				/>
				<PortalLandingSelect
					value={workspace.portalLandingSection}
					portalInstanceId={null}
					disabled={role === "VIEWER"}
					sections={{
						docs: workspace.helpCenterPublic ?? false,
						changelog: workspace.changelogPublic ?? false,
						roadmap: workspace.domainPublic ?? false,
						support: isSupportPublished,
					}}
					onChange={(portalLandingSection) =>
						void publish(
							{ id: workspace.id, portalLandingSection },
							"update-main-portal-landing",
							portalLandingSection !== null,
						)
					}
				/>
			</SettingsContainer>

			<div className={cn(settingsNavGroupClass, "mt-4")}>
				<SettingsNavRow
					icon={LinkIcon}
					title="Custom domain"
					description="Serve every portal from your own domain."
					link="/settings/portal/domain"
				/>
			</div>

			<SettingsSection
				title="Sections"
				description="Pick what the main portal shows. Each section is public once it is on."
				className="mb-3"
			/>
			<SettingsContainer className="flex flex-col gap-4 divide-y-0">
				<SettingItem
					title="Docs"
					description="Help center articles that belong to no other portal."
					action={
						<Switch
							name="helpCenterPublic"
							label={null}
							className="pr-1"
							checked={workspace.helpCenterPublic ?? false}
							onCheckedChange={(value) =>
								void publish(
									{ id: workspace.id, helpCenterPublic: value },
									"toggle-help-center-enabled",
									value,
								)
							}
						/>
					}
				/>
				<SettingItem
					title="Changelog"
					description="Entries that belong to no other portal."
					action={
						<Switch
							name="changelogPublic"
							label={null}
							className="pr-1"
							checked={workspace.changelogPublic ?? false}
							onCheckedChange={(value) =>
								void publish(
									{
										id: workspace.id,
										changelogPublic: value,
										changelogAutomaticDrafts:
											workspace.changelogAutomaticDrafts,
									},
									"toggle-changelog-enabled",
									value,
								)
							}
						/>
					}
				/>
				<SettingItem
					title="Feature requests"
					description="The public roadmap and the request form."
					action={
						<Switch
							name="domainPublic"
							label={null}
							className="pr-1"
							checked={workspace.domainPublic ?? false}
							onCheckedChange={(value) =>
								void publish(
									{ id: workspace.id, domainPublic: value },
									"toggle-roadmap-enabled",
									value,
								)
							}
						/>
					}
				/>
				<SettingItem
					title="Support portal"
					description="Signed-in customers see their own threads."
					action={
						canPublishSupport ? (
							<Switch
								name="customerPortalEnabled"
								label={null}
								className="pr-1"
								checked={isSupportPublished}
								onCheckedChange={(value) =>
									void publish(
										{
											id: workspace.id,
											customerPortalSettings: {
												isEnabled: value,
												teamRequests:
													workspace.customerPortalSettings?.teamRequests ??
													false,
											},
										},
										"toggle-customer-portal-enabled",
										value,
									)
								}
							/>
						) : (
							<UpgradeButton required={SUPPORT_PORTAL_PUBLISH_REQUIRED_PLANS} />
						)
					}
				/>
			</SettingsContainer>

			<PortalAuthSection
				values={workspace}
				onSave={({ googleDomain, ...rest }) => {
					void publish(
						{
							id: workspace.id,
							...rest,
							// The workspace column stores a cleared list as an empty string.
							...(googleDomain === undefined
								? {}
								: { googleDomain: googleDomain ?? "" }),
						},
						"update-portal-authentication",
						rest.isGoogleAuthRequired ?? workspace.isGoogleAuthRequired,
					)
				}}
			/>

			<PortalLinksSection
				values={workspace}
				onSave={(args) => {
					void publish(
						{ id: workspace.id, ...args },
						"toggle-logo-link",
						args.usePortalLogoUrl ?? workspace.usePortalLogoUrl,
					)
				}}
			/>

			<PortalContentCard portalInstanceId={null} />
		</>
	)
}
